import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { JourneyCard } from "@/components/messenger/JourneyCard";
import { UnifiedInbox } from "@/components/messenger/UnifiedInbox";
import { motion } from "framer-motion";
import { Loader2, Send, Phone, ArrowLeft, MessageCircle, Video, PhoneIncoming, MapPin, X, Users } from "lucide-react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { api, API } from "@/lib/api";
import { CallOverlay } from "@/components/messenger/CallOverlay";
import { LocationBubble, validCoords } from "@/components/messenger/LocationBubble";
import { ManualPinSheet } from "@/components/messenger/ManualPinSheet";
import { RING_TIMEOUT_MS, mediaErrorMessage, mediaSupportError } from "@/components/messenger/callHelpers";
import { decideServerError, decideSignal } from "@/components/messenger/callSignals";
import { iceForCall } from "@/lib/iceServers";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const relTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

function InboxTitle() {
  const { t } = useI18n();
  const [n, setN] = useState(0);
  useEffect(() => {
    const on = (e) => setN(e.detail?.total || 0);
    window.addEventListener("inbox:counts", on);
    return () => window.removeEventListener("inbox:counts", on);
  }, []);
  return <h1 className="heading text-2xl font-black text-stone-900">{t("inbox.title", "Inbox")}{n > 0 && <span className="ms-2 text-sm font-bold text-[#E05E36]" data-testid="inbox-total-unread">{t("inbox.n_unread", "{n} unread", { n })}</span>}</h1>;
}

export default function Messenger() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const { threadId } = useParams();
  const [newNvn, setNewNvn] = useState("");

  const startByNvn = async () => {
    const q = newNvn.trim();
    if (!/^\d{12}$/.test(q)) { toast.error("Enter a valid 12-digit NVN"); return; }
    try {
      const r = await api.post("/messenger/thread/start-by-nvn", { nvn: q });
      setNewNvn("");
      window.location.href = `/messenger/${r.data.conv_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start chat");
    }
  };

  if (authLoading) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;
  if (!user) return <div className="p-12 text-center text-stone-500">Sign in to use messenger.</div>;

  if (threadId) return <Thread threadId={threadId} onBack={() => window.history.back()} />;

  return (
    <div className="max-w-3xl mx-auto px-4 py-4" data-testid="messenger-page">
      <InboxTitle />
      <p className="text-xs text-stone-500 mb-4">{t("inbox.subtitle", "Messages plus service, payment and trip alerts — all in-app.")}</p>

      <div className="flex gap-2 mb-4">
        <input
          value={newNvn}
          onChange={e => setNewNvn(e.target.value.replace(/\D/g, "").slice(0, 12))}
          placeholder={t("inbox.nvn_ph", "Start chat by NVN (12 digits)")}
          className="flex-1 min-w-0 border border-stone-300 rounded-full px-4 py-2 text-sm outline-none focus:border-[#E05E36] font-mono"
          data-testid="messenger-start-nvn-input"
        />
        <button onClick={startByNvn} className="bg-[#E05E36] text-white rounded-full px-4 text-sm font-bold" data-testid="messenger-start-btn">
          {t("inbox.start", "Start")}
        </button>
      </div>
      <UnifiedInbox />
    </div>
  );
}

function Thread({ threadId, onBack }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [call, setCall] = useState(null);        // {mode:'idle'|'outgoing'|'incoming'|'live', kind:'audio'|'video', from?, offer?}
  const [callHudMuted, setCallHudMuted] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  const wsRef = useRef(null);
  const listRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  const [notFound, setNotFound] = useState(false);
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/messenger/thread/${threadId}`);
      setData(r.data);
      window.dispatchEvent(new Event("inbox:changed"));   // opening a thread reads it → refresh navbar badge
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else if (navigator.onLine) toast.error("Couldn't load thread");
    }
  }, [threadId]);

  useEffect(() => { load(); }, [load]);


  // WebSocket with polling fallback.
  // We fetch a short-lived ticket via XHR (cookies flow there) and pass it as `?ticket=`
  // on the WS URL. This is the only reliable way to auth cross-origin/behind-CDN WS
  // upgrades — the session cookie is httpOnly and may be stripped from the upgrade
  // request. If ticket fetch fails, we still open WS (server tries cookie fallback).
  useEffect(() => {
    let stopped = false;
    let ws;
    const backend = process.env.REACT_APP_BACKEND_URL || "";

    const openWs = async () => {
      let wsUrl = backend.replace(/^http/, "ws") + "/api/messenger/ws";
      try {
        const r = await api.get("/messenger/ws-ticket");
        if (r.data?.ticket) wsUrl += `?ticket=${encodeURIComponent(r.data.ticket)}`;
      } catch { /* fall through — server will try cookie/legacy */ }
      if (stopped) return;   // unmounted while fetching the ticket → don't leak a duplicate socket
      try {
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = async (ev) => {
          try {
            const msg = JSON.parse(ev.data);
            if (msg.type === "message" && msg.conv_id === threadId) {
              setData(d => d ? ({ ...d, messages: [...d.messages, msg.message] }) : d);
            } else if (msg.type === "webrtc_error") {
              // Server refused to relay (not a member / callee offline / rate-limited): never leave the caller on "Calling…".
              const d = decideServerError(msg.reason, callRef.current);
              if (d.type === "end") endCall(d.reason, false);
              else if (d.type === "toast_rate_limited") toast.error("Too many call attempts. Please wait a moment.");
            } else if (msg.type === "webrtc") {
              await handleWebRTCSignal(msg);
            } else if (msg.type === "location_share" && msg.conv_id === threadId) {
              setData(d => d ? ({ ...d, messages: [...d.messages, msg.message] }) : d);
            } else if (msg.type === "location_update" && msg.conv_id === threadId) {
              // Live pin moves (or ends) on the recipient's bubble in real time.
              setData(d => d ? ({ ...d, messages: d.messages.map(m => m.location?.session_id !== msg.session_id ? m : {
                ...m, location: msg.revoked ? { ...m.location, revoked: true } : { ...m.location, lat: msg.lat, lng: msg.lng, updated_at: msg.at } }) }) : d);
            }
          } catch {}
        };
        ws.onerror = () => { /* silently drop, polling will cover */ };
        ws.onclose = (e) => {
          if (stopped) return;
          // Auto-reconnect on abnormal close (1006, 1008, etc.) with backoff.
          if (e.code !== 1000) setTimeout(openWs, 2000);
        };
      } catch { /* WS failed → polling only */ }
    };

    openWs();
    const iv = setInterval(() => { if (!stopped) load(); }, 4000);
    return () => { stopped = true; clearInterval(iv); try { ws?.close(1000); } catch {} };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, load]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [data?.messages?.length]);

  // ---------------- WebRTC ----------------
  // ICE servers pulled from /api/config/ice-servers at mount — STUN by default,
  // TURN added automatically when TURN_URL env is set on the backend.
  // Streams live in refs and are (re)attached after every render, so <video> elements that mount
  // after getUserMedia/ontrack still get their srcObject (fixes the blank self-preview / remote panel).
  const remoteStreamRef = useRef(null);
  const pendingIceRef = useRef([]);
  const callRef = useRef(null);
  const ringTimerRef = useRef(null);
  if (call || callRef.current?.mode !== "starting") callRef.current = call;   // keep the pre-getUserMedia guard across re-renders

  useEffect(() => {
    if (!call) return;
    const rs = remoteStreamRef.current, ls = screenStreamRef.current || localStreamRef.current;
    if (remoteVideoRef.current && rs && remoteVideoRef.current.srcObject !== rs) remoteVideoRef.current.srcObject = rs;
    if (remoteAudioRef.current && rs && remoteAudioRef.current.srcObject !== rs) remoteAudioRef.current.srcObject = rs;
    if (localVideoRef.current && ls && localVideoRef.current.srcObject !== ls) localVideoRef.current.srcObject = ls;
  });

  const makePc = (target, iceServers) => {
    const pc = new RTCPeerConnection({ iceServers });
    pc.onicecandidate = (e) => {
      if (e.candidate) sendSignal(target, { kind: "ice", candidate: e.candidate.toJSON() });
    };
    pc.ontrack = (e) => {
      remoteStreamRef.current = e.streams[0] || new MediaStream([e.track]);
      setCall(c => (c ? { ...c, tracks: (c.tracks || 0) + 1 } : c));
    };
    pc.onconnectionstatechange = () => {
      if (pcRef.current !== pc) return;
      if (pc.connectionState === "connected") callRef.current = { ...callRef.current, wasConnected: true };
      setCall(c => (c ? { ...c, net: pc.connectionState, wasConnected: c.wasConnected || pc.connectionState === "connected" } : c));
      if (pc.connectionState === "failed") endCall(callRef.current?.wasConnected ? "lost" : "failed");
    };
    return pc;
  };

  const flushIce = async () => {
    const pc = pcRef.current;
    while (pc && pc.remoteDescription && pendingIceRef.current.length) {
      try { await pc.addIceCandidate(pendingIceRef.current.shift()); } catch { /* stale candidate */ }
    }
  };

  const sendSignal = (target, payload, convId = threadId) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      // conv_id lets the server verify both users share this conversation before relaying.
      ws.send(JSON.stringify({ type: "webrtc", target, conv_id: convId, signal: payload }));
    }
  };

  const cleanupMedia = () => {
    clearTimeout(ringTimerRef.current);
    localStreamRef.current?.getTracks().forEach(tr => tr.stop());
    screenStreamRef.current?.getTracks().forEach(tr => tr.stop());
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    localStreamRef.current = null;
    screenStreamRef.current = null;
    remoteStreamRef.current = null;
    pendingIceRef.current = [];
    setScreenSharing(false);
  };

  // reason → short "ended" state so the user sees why the call stopped; notify=false when the peer ended it.
  const endCall = (reason = "ended", notify = true) => {
    const c = callRef.current;
    if (!c || c.mode === "ended") return;
    const peer = c.peer || c.from;
    if (notify && peer) sendSignal(peer, { kind: c.mode === "incoming" ? "decline" : "hangup" });
    cleanupMedia();
    const next = { mode: "ended", kind: c.kind, reason };
    callRef.current = next;
    setCall(next);
    ringTimerRef.current = setTimeout(() => setCall(cur => (cur?.mode === "ended" ? null : cur)), 2500);
  };

  const startCall = async (kind = "audio") => {
    if (!data?.other?.user_id || (callRef.current && callRef.current.mode !== "ended")) return;
    const unsupported = mediaSupportError(kind);
    if (unsupported) { toast.error(unsupported); return; }
    const target = data.other.user_id;
    callRef.current = { mode: "starting", kind, peer: target };   // synchronous guard: a double-click can't start two calls
    let stream, offer;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: kind === "video" });
    } catch (e) { callRef.current = null; toast.error(mediaErrorMessage(e, kind)); return; }
    clearTimeout(ringTimerRef.current);
    localStreamRef.current = stream;
    const pc = makePc(target, await iceForCall(threadId));
    pcRef.current = pc;
    try {
      stream.getTracks().forEach(tr => pc.addTrack(tr, stream));
      offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
    } catch {
      cleanupMedia(); callRef.current = null; toast.error(`Could not start the ${kind} call.`); return;
    }
    const next = { mode: "outgoing", kind, peer: target, net: "new" };
    callRef.current = next;
    setCall(next);
    sendSignal(target, { kind: "offer", sdp: offer.sdp, callKind: kind });
    ringTimerRef.current = setTimeout(() => { if (callRef.current?.mode === "outgoing") endCall("no_answer"); }, RING_TIMEOUT_MS);
    api.post("/ecall/log", {
      callee_user_id: target, callee_nvn: data.other.nvn,
      callee_mobile: data.other.mobile, channel: "webrtc", direction: "outgoing", conv_id: threadId,
    }).catch(() => {});
  };

  const acceptCall = async () => {
    const c = callRef.current;
    if (!c || c.mode !== "incoming") return;
    const unsupported = mediaSupportError(c.kind);
    let stream;
    try {
      if (unsupported) throw Object.assign(new Error(unsupported), { name: "Unsupported" });
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: c.kind === "video" });
    } catch (e) {
      toast.error(unsupported || mediaErrorMessage(e, c.kind));
      endCall("media");
      return;
    }
    clearTimeout(ringTimerRef.current);
    localStreamRef.current = stream;
    const pc = makePc(c.from, await iceForCall(threadId));
    pcRef.current = pc;
    let ans;
    try {
      stream.getTracks().forEach(tr => pc.addTrack(tr, stream));
      await pc.setRemoteDescription({ type: "offer", sdp: c.offer });
      await flushIce();
      ans = await pc.createAnswer();
      await pc.setLocalDescription(ans);
    } catch { toast.error("Could not join the call."); endCall("failed"); return; }
    sendSignal(c.from, { kind: "answer", sdp: ans.sdp });
    const next = { ...c, mode: "live", peer: c.from, net: pc.connectionState, offer: undefined };
    callRef.current = next;
    setCall(next);
  };

  useEffect(() => () => { if (callRef.current && callRef.current.mode !== "ended") endCall("ended"); }, []); // eslint-disable-line

  // Reload / tab close mid-call: tell the peer right away instead of letting ICE time out.
  useEffect(() => {
    const onHide = () => {
      const c = callRef.current;
      const peer = c && c.mode !== "ended" && (c.peer || c.from);
      if (!peer) return;
      const kind = c.mode === "incoming" ? "decline" : "hangup";
      sendSignal(peer, { kind });
      try { navigator.sendBeacon?.(`${API}/ecall/webrtc/${peer}`, new Blob([JSON.stringify({ kind, conv_id: threadId })], { type: "application/json" })); } catch {}
    };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [threadId]); // eslint-disable-line

  // ---------------- Screen share (WebRTC getDisplayMedia) ----------------
  // Zoom/Meet-style: while in a live call, the presenter grabs their screen and
  // replaces the outgoing video track (or adds one on an audio-only call). The
  // remote peer sees it automatically via `pc.ontrack`. Note: native browser
  // remote-control (AnyDesk-style) is not permitted from a PWA — this is share-only.
  const startScreenShare = async () => {
    const other = data?.other?.user_id;
    if (!pcRef.current || !other) { toast.error("Start a call first"); return; }
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = stream.getVideoTracks()[0];
      if (!track) throw new Error("no track");
      screenStreamRef.current = stream;

      const pc = pcRef.current;
      const videoSender = pc.getSenders().find(s => s.track?.kind === "video");
      if (videoSender) {
        // Audio call: no existing video sender → addTrack + renegotiate. Video call:
        // just swap the outgoing track (no renegotiation needed for a same-kind swap).
        await videoSender.replaceTrack(track);
      } else {
        pc.addTrack(track, stream);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        sendSignal(other, { kind: "offer", sdp: offer.sdp, renegotiate: true });
      }
      // Show self-preview.
      if (localVideoRef.current) localVideoRef.current.srcObject = stream;
      // If the presenter clicks the browser's built-in "Stop sharing" bar.
      track.onended = () => stopScreenShare();
      setScreenSharing(true);
      // Force call.kind to 'video' so the overlay renders the video element.
      setCall(c => c ? ({ ...c, kind: "video" }) : c);
      toast.success("Screen shared");
    } catch (e) {
      if (e?.name === "NotAllowedError") toast.info("Screen share cancelled");
      else toast.error("Screen share failed");
    }
  };

  const stopScreenShare = async () => {
    const pc = pcRef.current;
    screenStreamRef.current?.getTracks().forEach(t => t.stop());
    screenStreamRef.current = null;
    setScreenSharing(false);
    // Restore camera track when available.
    const camTrack = localStreamRef.current?.getVideoTracks()[0];
    const videoSender = pc?.getSenders().find(s => s.track?.kind === "video");
    if (videoSender && camTrack) {
      try { await videoSender.replaceTrack(camTrack); } catch {}
      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;
    } else if (videoSender && !camTrack) {
      // Was audio-only call; drop the sender entirely to save bandwidth.
      try {
        pc.removeTrack(videoSender);
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const other = data?.other?.user_id;
        if (other) sendSignal(other, { kind: "offer", sdp: offer.sdp, renegotiate: true });
      } catch {}
      if (localVideoRef.current) localVideoRef.current.srcObject = null;
    }
  };

  const handleWebRTCSignal = async (msg) => {
    const sig = msg.signal || {};
    const from = msg.from_user_id;
    const pc = pcRef.current;
    const d = decideSignal(msg, { threadId, cur: callRef.current,
      pc: { exists: !!pc, hasRemote: !!pc?.remoteDescription, signalingState: pc?.signalingState } });
    switch (d.type) {
      case "toast_other_chat": toast.info(`Incoming ${d.callKind} call in another chat`); break;
      case "busy": sendSignal(from, { kind: "busy" }, d.convId); break;
      case "renegotiate":
        // Renegotiation offer (e.g. remote peer added a screen-share track mid-call).
        try {
          await pc.setRemoteDescription({ type: "offer", sdp: sig.sdp });
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          sendSignal(from, { kind: "answer", sdp: ans.sdp });
          const hasVideo = pc.getReceivers().some(r => r.track?.kind === "video" && r.track.readyState === "live" && !r.track.muted);
          if (hasVideo) setCall(c => c ? ({ ...c, kind: "video" }) : c);
        } catch { /* silently drop bad offers */ }
        break;
      case "incoming":
        clearTimeout(ringTimerRef.current);
        pendingIceRef.current = [];
        callRef.current = d.call;
        setCall(d.call);
        sendSignal(from, { kind: "ringing" });
        ringTimerRef.current = setTimeout(() => { if (callRef.current?.mode === "incoming") endCall("missed", false); }, RING_TIMEOUT_MS + 5000);
        break;
      case "ringing": setCall(c => (c?.mode === "outgoing" ? { ...c, ringing: true } : c)); break;
      case "answer":
        clearTimeout(ringTimerRef.current);
        try {
          await pc.setRemoteDescription({ type: "answer", sdp: sig.sdp });
          await flushIce();
          setCall(c => c ? { ...c, mode: "live", net: pcRef.current?.connectionState || c.net } : c);
        } catch { endCall("failed"); }
        break;
      case "ice_add": try { await pc.addIceCandidate(sig.candidate); } catch {} break;
      case "ice_buffer": pendingIceRef.current.push(sig.candidate); break;   // callee hasn't accepted yet
      case "end": endCall(d.reason, false); break;
      default: break;
    }
  };

  const send = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const r = await api.post(`/messenger/thread/${threadId}/send`, { body });
      setData(d => d ? ({ ...d, messages: [...d.messages, r.data] }) : d);
      setBody("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const callByTel = () => {
    if (!data?.other?.mobile) { toast.error("No number on file"); return; }
    window.location.href = `tel:${data.other.mobile}`;
    api.post("/ecall/log", {
      callee_user_id: data.other.user_id, callee_nvn: data.other.nvn,
      callee_mobile: data.other.mobile, channel: "tel", direction: "outgoing", conv_id: threadId,
    }).catch(() => {});
  };
  const callByWhatsApp = () => {
    if (!data?.other?.mobile) { toast.error("No number on file"); return; }
    const num = String(data.other.mobile).replace(/\D/g, "");
    window.open(`https://wa.me/${num}`, "_blank");
    api.post("/ecall/log", {
      callee_user_id: data.other.user_id, callee_nvn: data.other.nvn,
      callee_mobile: data.other.mobile, channel: "whatsapp", direction: "outgoing", conv_id: threadId,
    }).catch(() => {});
  };

  // ------------- Live location share -------------
  // Two modes: one-shot pin (ttl=0) or live-broadcast window (ttl=15/60/240 min).
  // Live mode keeps the browser's geolocation watcher open and PATCHes updates every
  // 15s so the recipient sees the pin move on their map. Auto-stops on ttl expiry,
  // manual revoke, or when the user leaves the thread.
  const [shareOpen, setShareOpen] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [liveSession, setLiveSession] = useState(null);
  const liveSessionRef = useRef(null);
  liveSessionRef.current = liveSession;
  const watchIdRef = useRef(null);
  const lastPingRef = useRef(0);

  const stopLiveShare = async () => {
    if (watchIdRef.current != null) {
      try { navigator.geolocation.clearWatch(watchIdRef.current); } catch {}
      watchIdRef.current = null;
    }
    const ls = liveSessionRef.current;
    if (ls) {
      try { await api.post(`/messenger/thread/${threadId}/location-session/${ls.session_id}/revoke`); } catch {}
    }
    setLiveSession(null);
    setSharing(false);
  };

  const [pinReason, setPinReason] = useState(null);   // non-null → manual pin sheet open
  const postLocation = async ({ lat, lng, accuracy_m, ttl, source }) => {
    if (!validCoords(lat, lng)) throw new Error("Invalid coordinates");
    const r = await api.post(`/messenger/thread/${threadId}/share-location`, {
      lat, lng, accuracy_m, ttl_minutes: ttl, source,
      label: source === "manual" ? "Pinned on map" : ttl === 0 ? "Current location" : `Live for ${ttl} min`,
    });
    setData(d => d ? ({ ...d, messages: [...d.messages, r.data] }) : d);
    return r.data;
  };

  const sendManualPin = async (loc) => {
    setPinReason(null);
    try {
      await postLocation({ lat: Number(loc.lat), lng: Number(loc.lng), ttl: 0, source: loc.source === "gps" ? "device" : "manual" });
      toast.success("Location shared.");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Could not share location"); }
  };

  const shareLocation = async (ttlMinutes) => {
    setShareOpen(false);
    // Never substitute a city/IP guess for GPS: fall back to an explicit, user-chosen pin.
    if (!window.isSecureContext) { setPinReason("Device location needs a secure (HTTPS) connection. Pick the place on the map instead."); return; }
    if (!navigator.geolocation) { setPinReason("This browser can't read device location. Pick the place on the map instead."); return; }
    setSharing(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude, accuracy } = pos.coords;
          const msg = await postLocation({ lat: latitude, lng: longitude, accuracy_m: accuracy, ttl: ttlMinutes, source: "device" });
          if (ttlMinutes > 0 && msg.location?.session_id) {
            const session_id = msg.location.session_id;
            liveSessionRef.current = { session_id, expires_at: msg.location.expires_at };
            setLiveSession(liveSessionRef.current);
            // Start the watcher — throttle POSTs to 1/15s so we don't spam Mongo.
            // Trailing throttle (1 ping / 15 s): the latest position is always sent, never dropped.
            let latest = null, timer = null;
            const ping = () => {
              timer = null;
              if (!latest || watchIdRef.current == null) return;
              lastPingRef.current = Date.now();
              const c = latest.coords;
              api.post(`/messenger/thread/${threadId}/location-update`, {
                session_id, lat: c.latitude, lng: c.longitude, accuracy_m: c.accuracy,
              }).catch((e) => { if (e?.response?.status === 410) stopLiveShare(); });
            };
            watchIdRef.current = navigator.geolocation.watchPosition(
              (p) => {
                latest = p;
                const wait = 15000 - (Date.now() - lastPingRef.current);
                if (wait <= 0) ping();
                else if (!timer) timer = setTimeout(ping, wait);
              },
              () => {},
              { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
            );
            toast.success(`Live-sharing for ${ttlMinutes} min. Tap "Stop" to end.`);
          } else {
            toast.success("Location shared.");
            setSharing(false);
          }
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Could not share location");
          setSharing(false);
        }
      },
      (err) => {
        setSharing(false);
        setPinReason(err.code === 1 ? "Location permission was denied. You can pick the place on the map instead."
          : err.code === 3 ? "Getting your location timed out. Pick the place on the map instead."
          : "Your device location is unavailable. Pick the place on the map instead.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 },
    );
  };

  // Closing the tab ends a live share right away (not only at TTL expiry).
  useEffect(() => {
    if (!liveSession) return;
    const url = `${API}/messenger/thread/${threadId}/location-session/${liveSession.session_id}/revoke`;
    const onHide = () => { try { navigator.sendBeacon?.(url); } catch {} };
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [liveSession, threadId]);

  // Auto-stop live share if the sharer leaves the thread.
  useEffect(() => () => { if (watchIdRef.current != null) stopLiveShare(); }, []); // eslint-disable-line

  if (notFound) return (
    <div className="p-12 text-center text-stone-600" data-testid="thread-not-found">
      <p className="font-bold mb-2">{t("inbox.thread_not_found", "Conversation not found")}</p>
      <p className="text-sm mb-4">{t("inbox.thread_not_found_hint", "It may have been removed, or you're not part of it.")}</p>
      <Link to="/messenger" className="underline font-bold text-sky-700" data-testid="thread-not-found-back">{t("inbox.back", "Back to inbox")}</Link>
    </div>
  );
  if (!data) return <div className="p-12 text-center"><Loader2 className="inline animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100dvh-10rem)] min-h-[420px]" data-testid="messenger-thread">
      <div className="flex items-center gap-2 p-3 border-b border-stone-200 bg-white sticky top-0 z-10">
        <button onClick={onBack} className="p-2 text-stone-600"><ArrowLeft size={18} /></button>
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E05E36] to-[#2D5A27] grid place-items-center text-white font-bold">
          {data.other?.name?.[0] || "U"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">{data.other?.name || "User"}</div>
          {data.other?.nvn && <div className="font-mono text-[10px] text-stone-500">NVN {data.other.nvn}</div>}
        </div>
        <button onClick={callByTel} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full" data-testid="thread-call-btn" title="Phone call">
          <Phone size={18} />
        </button>
        {data.context?.kind === "rideshare_convoy" && (
          <ConvoyLiveMapButton convId={threadId}/>
        )}
        <button onClick={() => startCall("audio")} className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-full" data-testid="thread-webrtc-audio" title="Voice call (WebRTC)">
          <PhoneIncoming size={18} />
        </button>
        <button onClick={() => startCall("video")} className="p-2 text-fuchsia-600 hover:bg-fuchsia-50 rounded-full" data-testid="thread-webrtc-video" title="Video call (WebRTC)">
          <Video size={18} />
        </button>
        <button onClick={callByWhatsApp} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-full" data-testid="thread-wa-btn" title="WhatsApp">
          <Send size={18} />
        </button>
        <button
          onClick={() => (sharing ? stopLiveShare() : setShareOpen(true))}
          className={`p-2 rounded-full ${sharing ? "text-red-600 bg-red-50 animate-pulse" : "text-sky-600 hover:bg-sky-50"}`}
          data-testid="thread-location-btn"
          title={sharing ? "Stop live location share" : "Share location"}
        >
          <MapPin size={18} />
        </button>
      </div>

      {/* Share-location bottom sheet */}
      {shareOpen && (
        <div className="fixed inset-0 bg-black/40 grid place-items-end md:place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => setShareOpen(false)} data-testid="share-location-sheet">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg text-stone-900">Share your location</div>
              <button onClick={() => setShareOpen(false)} className="p-1 text-stone-500 hover:text-stone-800" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-stone-600 mb-4">Pick how long the recipient can see your location. You can stop live sharing anytime.</p>
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => shareLocation(0)} className="border border-stone-300 rounded-xl py-3 hover:border-[#E05E36] hover:bg-orange-50 font-semibold text-sm" data-testid="share-loc-pin">
                <MapPin size={16} className="inline mr-1" /> One-shot pin
              </button>
              <button onClick={() => shareLocation(15)} className="border border-stone-300 rounded-xl py-3 hover:border-[#E05E36] hover:bg-orange-50 font-semibold text-sm" data-testid="share-loc-15">
                Live · 15 min
              </button>
              <button onClick={() => shareLocation(60)} className="border border-stone-300 rounded-xl py-3 hover:border-[#E05E36] hover:bg-orange-50 font-semibold text-sm" data-testid="share-loc-60">
                Live · 1 hour
              </button>
              <button onClick={() => shareLocation(240)} className="border border-stone-300 rounded-xl py-3 hover:border-[#E05E36] hover:bg-orange-50 font-semibold text-sm" data-testid="share-loc-240">
                Live · 4 hours
              </button>
              <button onClick={() => { setShareOpen(false); setPinReason(""); }} className="col-span-2 border border-dashed border-stone-300 rounded-xl py-3 hover:border-sky-500 hover:bg-sky-50 font-semibold text-sm" data-testid="share-loc-manual">
                Pick a place on the map
              </button>
            </div>
          </div>
        </div>
      )}

      {pinReason !== null && <ManualPinSheet reason={pinReason} onPick={sendManualPin} onClose={() => setPinReason(null)} />}
      {call && (
        <CallOverlay call={call} name={data?.other?.name} refs={{ remoteAudio: remoteAudioRef, remoteVideo: remoteVideoRef, localVideo: localVideoRef }}
                     onAccept={acceptCall} onDecline={() => endCall("declined")} onEnd={() => endCall("ended")}
                     screenSharing={screenSharing} onToggleShare={screenSharing ? stopScreenShare : startScreenShare} />
      )}

      <JourneyCard convId={threadId} refreshKey={data?.messages?.length} />
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 bg-stone-50">
        {data.messages.map((m) => {
          const mine = m.sender_id === user.user_id;
          const isCall = m.channel === "call_log";
          const isLoc = m.channel === "location" && m.location;
          return (
            <div key={m.msg_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              {isLoc ? (
                <LocationBubble m={m} mine={mine} when={relTime(m.created_at)} />
              ) : (
                <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#E05E36] text-white" : "bg-white border border-stone-200 text-stone-800"} ${isCall ? "italic text-xs opacity-75" : ""}`}>
                  {isCall && <Phone size={11} className="inline mr-1" />}
                  {m.body}
                  <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-stone-400"}`}>{relTime(m.created_at)}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-3 border-t border-stone-200 bg-white flex items-center gap-2">
        <input
          value={body}
          onChange={e => setBody(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !sending && send()}
          placeholder={t("inbox.type_message", "Type a message…")}
          className="flex-1 border border-stone-300 rounded-full px-4 py-2 text-sm outline-none focus:border-[#E05E36]"
          data-testid="thread-input"
        />
        <button
          onClick={send}
          disabled={sending}
          className="bg-[#E05E36] text-white rounded-full p-2.5"
          data-testid="thread-send-btn"
        >
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}



// ─── Convoy Live-Map (RideShare Slice 10) ───────────────────────────────
function ConvoyLiveMapButton({ convId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button onClick={() => setOpen(true)}
              className="p-2 text-purple-600 hover:bg-purple-50 rounded-full"
              data-testid="convoy-live-map-btn" title="Live convoy map">
        <Users size={18}/>
      </button>
      {open && <ConvoyLiveMapModal convId={convId} onClose={() => setOpen(false)}/>}
    </>
  );
}

function ConvoyLiveMapModal({ convId, onClose }) {
  const [data, setData]   = useState(null);
  const [error, setError] = useState(null);
  const wrapRef = useRef(null);
  const mapRef  = useRef(null);
  const carRefs = useRef({});             // trip_id → maplibre.Marker

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/rideshare/convoys/${convId}/live`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Cannot load live map");
      }
    };
    load();
    const id = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [convId]);

  // Init the map once we have any coord.
  useEffect(() => {
    if (!wrapRef.current || mapRef.current || !data?.trips?.length) return;
    const withGps = data.trips.filter(t => t.current_lat != null);
    if (withGps.length === 0) return;
    const center = [withGps[0].current_lng, withGps[0].current_lat];
    const map = new maplibregl.Map({
      container: wrapRef.current,
      style: "https://tiles.openfreemap.org/styles/liberty",
      center, zoom: 9, attributionControl: false,
    });
    map.on("error", (e) => {
      if (String(e?.error?.message || "").includes("tiles")) {
        try { map.setStyle("https://demotiles.maplibre.org/style.json"); } catch { /* ignore */ }
      }
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");

    // Drop the shared destination pin (only one — both cars head there).
    const to = data.trips.find(t => t.to_lat != null);
    if (to) {
      new maplibregl.Marker({ color: "#dc2626" })
        .setLngLat([to.to_lng, to.to_lat])
        .setPopup(new maplibregl.Popup().setText(`Drop-off · ${to.to_city}`))
        .addTo(map);
    }

    // Two cars with distinct emoji glyphs.
    const glyphs = ["🚗", "🚙"];
    data.trips.forEach((tp, i) => {
      if (tp.current_lat == null) return;
      const el = document.createElement("div");
      el.setAttribute("data-testid", `convoy-car-${tp.trip_id}`);
      el.style.cssText = "font-size:30px;text-shadow:0 2px 6px rgba(0,0,0,0.5);";
      el.textContent = glyphs[i] || "🚗";
      carRefs.current[tp.trip_id] = new maplibregl.Marker({ element: el, anchor: "center" })
        .setLngLat([tp.current_lng, tp.current_lat])
        .setPopup(new maplibregl.Popup().setText(`${tp.driver_first} · ${tp.from_city} → ${tp.to_city}`))
        .addTo(map);
    });

    // Fit both cars + destination on first load.
    try {
      const b = new maplibregl.LngLatBounds();
      withGps.forEach(t => b.extend([t.current_lng, t.current_lat]));
      if (to) b.extend([to.to_lng, to.to_lat]);
      map.fitBounds(b, { padding: 60, duration: 400, maxZoom: 11 });
    } catch { /* ignore */ }

    mapRef.current = map;
    return () => { try { map.remove(); } catch { /* ignore */ } mapRef.current = null; carRefs.current = {}; };
  }, [data]);

  // Move markers on subsequent polls.
  useEffect(() => {
    if (!mapRef.current || !data?.trips) return;
    data.trips.forEach(tp => {
      if (tp.current_lat == null) return;
      const m = carRefs.current[tp.trip_id];
      if (m) m.setLngLat([tp.current_lng, tp.current_lat]);
    });
  }, [data]);

  return (
    <div className="fixed inset-0 z-[80] bg-stone-900 flex flex-col" onClick={onClose} data-testid="convoy-live-map-modal">
      <div className="flex items-center justify-between px-4 py-3 bg-purple-700 text-white" onClick={e => e.stopPropagation()}>
        <div className="font-bold flex items-center gap-2"><Users size={16}/> Live convoy map</div>
        <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="convoy-live-map-close"><X size={18}/></button>
      </div>
      {error && (
        <div className="flex-1 grid place-items-center text-rose-100 text-sm p-4" data-testid="convoy-live-map-error">
          {error}
        </div>
      )}
      {!error && (
        <>
          <div ref={wrapRef} className="flex-1" onClick={e => e.stopPropagation()} data-testid="convoy-live-map-canvas"/>
          <div className="bg-white px-4 py-3 grid grid-cols-2 gap-2" onClick={e => e.stopPropagation()}>
            {data?.trips?.map((tp, i) => (
              <div key={tp.trip_id} className="border rounded-xl px-3 py-2 text-xs" data-testid={`convoy-legend-${tp.trip_id}`}>
                <div className="font-bold text-stone-800 flex items-center gap-1">
                  <span className="text-lg">{i === 0 ? "🚗" : "🚙"}</span> {tp.driver_first}
                </div>
                <div className="text-[10px] text-stone-500">{tp.from_city} → {tp.to_city}</div>
                <div className="mt-1 flex flex-wrap gap-2 text-[11px]">
                  {tp.eta_minutes != null && <span className="text-emerald-700 font-bold">ETA {Math.round(tp.eta_minutes)}m</span>}
                  {tp.speed_kmh != null && <span className="text-stone-500">{Math.round(tp.speed_kmh)} km/h</span>}
                  {tp.remaining_km != null && <span className="text-stone-500">{tp.remaining_km} km left</span>}
                </div>
              </div>
            ))}
            {data?.apart_km != null && (
              <div className="col-span-2 text-center text-[11px] text-purple-700 font-bold" data-testid="convoy-apart-km">
                Sister car is {data.apart_km} km away
              </div>
            )}
            {(!data || data.trips?.every(t => t.current_lat == null)) && (
              <div className="col-span-2 text-center text-sm text-stone-500">Waiting for both drivers to broadcast GPS…</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
